import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import API from '../services/api';

function AIMediaLibrary({ isEmbedded = false }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);  // ✅ NEW
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [uploadMode, setUploadMode] = useState('file');

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    fileUrl: '',
    fileName: '',
    fileType: 'image',
    triggerKeywords: '',
    aiInstruction: '',
    caption: ''
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    triggerKeywords: '',
    aiInstruction: '',
    caption: '',
    isActive: true
  });

  const hasSubscription = localStorage.getItem('subscriptionStatus') === 'active';
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const response = await API.get('/ai-media');
      setMedia(response.data.data || []);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Failed to load media library');
    }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file, fileName: file.name.split('.')[0] });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!hasSubscription && !isSuperAdmin) {
      toast.error('Active subscription required!');
      setTimeout(() => window.location.href = '/pricing', 1500);
      return;
    }

    const toastId = toast.loading('Uploading media...');

    try {
      if (uploadMode === 'file') {
        // ✅ FILE UPLOAD MODE
        const formData = new FormData();
        formData.append('file', uploadForm.file);
        formData.append('fileName', uploadForm.fileName);
        formData.append('fileType', uploadForm.fileType);
        formData.append('triggerKeywords', uploadForm.triggerKeywords);  // ✅ String form (backend handles split)
        formData.append('aiInstruction', uploadForm.aiInstruction);
        formData.append('caption', uploadForm.caption);

        await API.post('/ai-media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // ✅ URL UPLOAD MODE
        await API.post('/ai-media/url', {
          fileUrl: uploadForm.fileUrl,
          fileName: uploadForm.fileName,
          fileType: uploadForm.fileType,
          mimeType: uploadForm.fileType === 'image' ? 'image/jpeg' :
            uploadForm.fileType === 'video' ? 'video/mp4' :
              uploadForm.fileType === 'audio' ? 'audio/mpeg' :
                'application/pdf',
          triggerKeywords: uploadForm.triggerKeywords,  // ✅ Send as string (backend will handle)
          aiInstruction: uploadForm.aiInstruction,
          caption: uploadForm.caption
        });
      }

      // ✅ SUCCESS MESSAGE
      toast.success('Media uploaded successfully! 🎉', { id: toastId });
      setShowUploadModal(false);
      resetUploadForm();
      loadMedia();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Upload failed', { id: toastId });
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const toastId = toast.loading('Updating media...');

    try {
      await API.put(`/ai-media/${selectedMedia.id}`, {
        triggerKeywords: editForm.triggerKeywords.split(',').map(k => k.trim()),
        aiInstruction: editForm.aiInstruction,
        caption: editForm.caption,
        isActive: editForm.isActive
      });

      toast.success('Media updated successfully!', { id: toastId });
      setShowEditModal(false);
      setSelectedMedia(null);
      loadMedia();
    } catch (error) {
      toast.error('Failed to update media', { id: toastId });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ Delete this media? This action cannot be undone.')) return;

    const toastId = toast.loading('Deleting media...');
    try {
      await API.delete(`/ai-media/${id}`);
      toast.success('Media deleted!', { id: toastId });
      loadMedia();
    } catch (error) {
      toast.error('Failed to delete media', { id: toastId });
    }
  };

  const openEditModal = (item) => {
    setSelectedMedia(item);
    setEditForm({
      triggerKeywords: (() => {
        if (typeof item.trigger_keywords === 'string') {
          try {
            const parsed = JSON.parse(item.trigger_keywords);
            return Array.isArray(parsed) ? parsed.join(', ') : item.trigger_keywords;
          } catch {
            return item.trigger_keywords;
          }
        }
        return (item.trigger_keywords || []).join(', ');
      })(),
      aiInstruction: item.ai_instruction || '',
      caption: item.caption || '',
      isActive: item.is_active
    });
    setShowEditModal(true);
  };

  // ✅ NEW: Open Preview Modal
  const openPreviewModal = (item) => {
    setSelectedMedia(item);
    setShowPreviewModal(true);
  };

  const resetUploadForm = () => {
    setUploadForm({
      file: null,
      fileUrl: '',
      fileName: '',
      fileType: 'image',
      triggerKeywords: '',
      aiInstruction: '',
      caption: ''
    });
  };

  const filteredMedia = media
    .filter(item => filterType === 'all' || item.file_type === filterType)
   .filter(item => {
  const kws = (() => {
    if (typeof item.trigger_keywords === 'string') {
      try {
        const parsed = JSON.parse(item.trigger_keywords);
        return Array.isArray(parsed)
          ? parsed.map(k => k.trim().toLowerCase())
          : item.trigger_keywords.split(',').map(k => k.trim().toLowerCase());
      } catch {
        return item.trigger_keywords.split(',').map(k => k.trim().toLowerCase());
      }
    }
    return item.trigger_keywords || [];
  })();
  return item.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kws.some(k => k.includes(searchTerm.toLowerCase()));
});

  const getFileIcon = (type) => {
    const icons = {
      image: '🖼️',
      document: '📄',
      video: '🎥',
      audio: '🎵'
    };
    return icons[type] || '📁';
  };

  // ✅ NEW: Get file URL helper
  const getFileUrl = (item) => {
    if (item.file_path) {
      return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${item.file_path}`;
    }
    return item.file_url;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner">⏳</div>
          <p className="loading-text">Loading media library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? "" : "ai-media-page"} style={isEmbedded ? { padding: '28px 32px', background: 'transparent' } : {}}>

      {/* Header - Only show in standalone mode */}
      {!isEmbedded && (
        <div className="ai-media-header">
          <div>
            <h1 className="ai-media-title">📂 AI Media Library</h1>
            <p className="ai-media-subtitle">Upload images, documents, and videos that AI will send automatically based on keywords</p>
          </div>
          <button onClick={() => setShowUploadModal(true)} className="ai-media-btn-upload">
            ➕ Upload Media
          </button>
        </div>
      )}

      {/* Compact header for embedded mode */}
      {isEmbedded && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h3 style={{
              margin: '0 0 4px 0',
              fontSize: '18px',
              fontWeight: '700',
              color: '#1e293b'
            }}>
              Your Media Files
            </h3>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: '#64748b'
            }}>
              {media.length} file{media.length !== 1 ? 's' : ''} uploaded
            </p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #1e3a5f, #2d5986)',

              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(30, 58, 95, 0.3)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
            }}
          >
            ➕ Upload New Media
          </button>
        </div>
      )}

      {/* No subscription warning */}
      {!isSuperAdmin && !hasSubscription && (
        <div className="ai-media-warning">
          <p className="ai-media-warning-text">🔒 Active subscription required to upload and manage AI media</p>
          <button onClick={() => window.location.href = '/pricing'} className="ai-media-warning-btn">
            View Plans →
          </button>
        </div>
      )}

      {/* Search & Filters */}
      <div className="ai-media-controls">
        <input
          type="text"
          placeholder="🔍 Search by name or keywords..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="ai-media-search"
        />
        <div className="ai-media-filters">
          {['all', 'image', 'document', 'video', 'audio'].map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`ai-media-filter-btn ${filterType === type ? 'ai-media-filter-active' : ''}`}
            >
              {type === 'all' ? '📁 All' : `${getFileIcon(type)} ${type.charAt(0).toUpperCase() + type.slice(1)}s`}
            </button>
          ))}
        </div>
      </div>

      {/* Media Grid */}
      {filteredMedia.length === 0 ? (
        <div className="ai-media-empty">
          <div className="ai-media-empty-icon">📂</div>
          <p className="ai-media-empty-text">No media files found</p>
          <button onClick={() => setShowUploadModal(true)} className="ai-media-btn-upload">
            Upload Your First Media
          </button>
        </div>
      ) : (
        <div className="ai-media-grid">
          {filteredMedia.map(item => (
            <div key={item.id} className="ai-media-card">

              {/* Preview - ✅ UPDATED with better fallback */}
              <div className="ai-media-preview" onClick={() => openPreviewModal(item)} style={{ cursor: 'pointer' }}>
                {item.file_type === 'image' ? (
                  <img
                    src={getFileUrl(item)}
                    alt={item.file_name}
                    className="ai-media-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="ai-media-icon-fallback" style={{ display: item.file_type === 'image' ? 'none' : 'flex' }}>
                  <span>{getFileIcon(item.file_type)}</span>
                </div>
                {/* ✅ Click to preview hint */}
                <div className="ai-media-preview-hint">👁️ Click to preview</div>
              </div>

              {/* Info */}
              <div className="ai-media-info">
                <div className="ai-media-name-row">
                  <h3 className="ai-media-name">{item.file_name}</h3>
                  <span className={`ai-media-status ${item.is_active ? 'ai-media-status-active' : 'ai-media-status-inactive'}`}>
                    {item.is_active ? '✅' : '⏸️'}
                  </span>
                </div>

                <div className="ai-media-meta">
                  <span className="ai-media-meta-item">📊 {item.usage_count} uses</span>
                  {item.file_size && <span className="ai-media-meta-item">💾 {(item.file_size / 1024).toFixed(1)} KB</span>}
                </div>

                {(() => {
                  const kws = typeof item.trigger_keywords === 'string'
                    ? item.trigger_keywords.split(',').map(k => k.trim()).filter(Boolean)
                    : item.trigger_keywords || [];
                  return kws.length > 0 ? (
                    <div className="ai-media-keywords">
                      {kws.slice(0, 3).map((keyword, idx) => (
                        <span key={idx} className="ai-media-keyword">{keyword}</span>
                      ))}
                      {kws.length > 3 && (
                        <span className="ai-media-keyword-more">+{kws.length - 3}</span>
                      )}
                    </div>
                  ) : null;
                })()}

                {item.caption && (
                  <p className="ai-media-caption">{item.caption}</p>
                )}
              </div>

              {/* Actions - ✅ UPDATED with Preview button */}
              <div className="ai-media-actions">
                <button onClick={() => openPreviewModal(item)} className="ai-media-btn-preview">
                  👁️ Preview
                </button>
                <button onClick={() => openEditModal(item)} className="ai-media-btn-edit">
                  ✏️ Edit
                </button>
                <button onClick={() => handleDelete(item.id)} className="ai-media-btn-delete">
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="ai-media-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-media-modal-header">
              <h2>📤 Upload Media</h2>
              <button onClick={() => setShowUploadModal(false)} className="ai-media-modal-close">✕</button>
            </div>

            <div className="ai-media-modal-body">

              {/* Mode Toggle */}
              <div className="ai-media-mode-toggle">
                <button
                  onClick={() => setUploadMode('file')}
                  className={`ai-media-mode-btn ${uploadMode === 'file' ? 'ai-media-mode-active' : ''}`}
                >
                  📁 Upload File
                </button>
                <button
                  onClick={() => setUploadMode('url')}
                  className={`ai-media-mode-btn ${uploadMode === 'url' ? 'ai-media-mode-active' : ''}`}
                >
                  🔗 Add URL
                </button>
              </div>

              <form onSubmit={handleUpload}>

                {uploadMode === 'file' ? (
                  <div className="form-group">
                    <label className="form-label">Select File</label>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf,.doc,.docx,video/*,audio/*"
                      className="form-input"
                      required
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label className="form-label">File URL</label>
                    <input
                      type="url"
                      value={uploadForm.fileUrl}
                      onChange={(e) => setUploadForm({ ...uploadForm, fileUrl: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="form-input"
                      required
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Display Name</label>
                  <input
                    type="text"
                    value={uploadForm.fileName}
                    onChange={(e) => setUploadForm({ ...uploadForm, fileName: e.target.value })}
                    placeholder="margarita-pizza"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">File Type</label>
                  <select
                    value={uploadForm.fileType}
                    onChange={(e) => setUploadForm({ ...uploadForm, fileType: e.target.value })}
                    className="form-input"
                  >
                    <option value="image">Image</option>
                    <option value="document">Document</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Trigger Keywords (comma-separated)</label>
                  <input
                    type="text"
                    value={uploadForm.triggerKeywords}
                    onChange={(e) => setUploadForm({ ...uploadForm, triggerKeywords: e.target.value })}
                    placeholder="margarita, margherita pizza, pizza"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">AI Instruction</label>
                  <textarea
                    value={uploadForm.aiInstruction}
                    onChange={(e) => setUploadForm({ ...uploadForm, aiInstruction: e.target.value })}
                    placeholder="Send when user asks about Margarita pizza"
                    rows="2"
                    className="form-textarea"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Caption (sent with media)</label>
                  <textarea
                    value={uploadForm.caption}
                    onChange={(e) => setUploadForm({ ...uploadForm, caption: e.target.value })}
                    placeholder="🍕 Margarita Pizza - ₹299"
                    rows="2"
                    className="form-textarea"
                  />
                </div>

                <button type="submit" className="ai-media-btn-submit">
                  📤 Upload Media
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMedia && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="ai-media-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-media-modal-header">
              <h2>✏️ Edit Media</h2>
              <button onClick={() => setShowEditModal(false)} className="ai-media-modal-close">✕</button>
            </div>

            <div className="ai-media-modal-body">
              <form onSubmit={handleEdit}>

                <div className="form-group">
                  <label className="form-label">Trigger Keywords</label>
                  <input
                    type="text"
                    value={editForm.triggerKeywords}
                    onChange={(e) => setEditForm({ ...editForm, triggerKeywords: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">AI Instruction</label>
                  <textarea
                    value={editForm.aiInstruction}
                    onChange={(e) => setEditForm({ ...editForm, aiInstruction: e.target.value })}
                    rows="2"
                    className="form-textarea"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Caption</label>
                  <textarea
                    value={editForm.caption}
                    onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                    rows="2"
                    className="form-textarea"
                  />
                </div>

                <div className="form-group">
                  <label className="ai-media-checkbox-label">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    />
                    <span>Active (AI can send this media)</span>
                  </label>
                </div>

                <button type="submit" className="ai-media-btn-submit">
                  💾 Save Changes
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: Preview Modal */}
      {showPreviewModal && selectedMedia && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="ai-media-preview-modal" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="ai-media-preview-header">
              <div>
                <h2 className="ai-media-preview-title">👁️ {selectedMedia.file_name}</h2>
                <p className="ai-media-preview-type">{getFileIcon(selectedMedia.file_type)} {selectedMedia.file_type.toUpperCase()}</p>
              </div>
              <button onClick={() => setShowPreviewModal(false)} className="ai-media-modal-close">✕</button>
            </div>

            {/* Preview Content */}
            <div className="ai-media-preview-content">
              {selectedMedia.file_type === 'image' && (
                <img
                  src={getFileUrl(selectedMedia)}
                  alt={selectedMedia.file_name}
                  className="ai-media-preview-img"
                />
              )}

              {selectedMedia.file_type === 'video' && (
                <video controls className="ai-media-preview-video">
                  <source src={getFileUrl(selectedMedia)} />
                  Your browser does not support video playback.
                </video>
              )}

              {selectedMedia.file_type === 'audio' && (
                <audio controls className="ai-media-preview-audio">
                  <source src={getFileUrl(selectedMedia)} />
                  Your browser does not support audio playback.
                </audio>
              )}

              {selectedMedia.file_type === 'document' && (
                <div className="ai-media-preview-doc">
                  <div className="ai-media-preview-doc-icon">📄</div>
                  <p className="ai-media-preview-doc-text">Document Preview</p>
                  <a
                    href={getFileUrl(selectedMedia)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ai-media-preview-doc-link"
                  >
                    🔗 Open Document
                  </a>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="ai-media-preview-details">
              {selectedMedia.caption && (
                <div className="ai-media-preview-detail-item">
                  <strong>Caption:</strong>
                  <p>{selectedMedia.caption}</p>
                </div>
              )}

              {(() => {
                const kws = typeof selectedMedia.trigger_keywords === 'string'
                  ? selectedMedia.trigger_keywords.split(',').map(k => k.trim()).filter(Boolean)
                  : selectedMedia.trigger_keywords || [];
                return kws.length > 0 ? (
                  <div className="ai-media-preview-detail-item">
                    <strong>Keywords:</strong>
                    <div className="ai-media-keywords">
                      {kws.map((keyword, idx) => (
                        <span key={idx} className="ai-media-keyword">{keyword}</span>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              <div className="ai-media-preview-meta">
                <span>📊 Used {selectedMedia.usage_count} times</span>
                {selectedMedia.file_size && <span>💾 {(selectedMedia.file_size / 1024).toFixed(1)} KB</span>}
                <span>{selectedMedia.is_active ? '✅ Active' : '⏸️ Inactive'}</span>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default AIMediaLibrary;